/**
 * MODULE: _shared/frontend-shell/src/components/generative-ui/EmailTemplateChoiceCard.tsx
 * PURPOSE: Clickable Sales Hub email template picker in assistant chat (disambiguation).
 */

import React from 'react';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import type { EmailTemplateChoice } from '../../hooks/assistant/types';

interface EmailTemplateChoiceCardProps {
  choice: EmailTemplateChoice;
  onSelect: (templateId: string, title: string, continuation?: Record<string, unknown>) => void;
}

export const EmailTemplateChoiceCard: React.FC<EmailTemplateChoiceCardProps> = ({
  choice,
  onSelect,
}) => {
  return (
    <div
      className="mt-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden"
      data-testid="assistant-email-template-choice"
    >
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div className="flex items-center gap-2">
          <EnvelopeIcon className="w-5 h-5 text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Choose email template</h3>
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{choice.message}</p>
      </div>
      <ul className="divide-y divide-gray-100 dark:divide-gray-700">
        {choice.matches.map((row, idx) => {
          const title = row.name || row.title || 'Email template';
          const uses = row.uses ?? 0;
          const maxUses = Math.max(...choice.matches.map((m) => m.uses ?? 0), 0);
          const isTopPick = uses > 0 && uses === maxUses;
          return (
            <li key={row.id || idx}>
              <button
                type="button"
                className="w-full text-left px-4 py-3 hover:bg-blue-50 dark:hover:bg-gray-700/60 transition-colors"
                onClick={() => onSelect(row.id, title, choice.continuation)}
                data-testid={`assistant-email-template-option-${idx + 1}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      <span className="text-gray-400 mr-2">{idx + 1}.</span>
                      {title}
                      {isTopPick ? (
                        <span className="ml-2 text-xs font-normal text-blue-600 dark:text-blue-400">
                          Recommended
                        </span>
                      ) : null}
                    </p>
                    {row.subject ? (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                        Subject: {row.subject}
                      </p>
                    ) : null}
                  </div>
                  {uses > 0 ? (
                    <span className="shrink-0 text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                      Used {uses}×
                    </span>
                  ) : null}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
